import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import BasicEmbed from './BasicEmbed';
import className from 'classnames/bind';
import { AcfMediaFragment } from '@graphqlTypes';
import { IconButton, Image } from "@components";
import { EASE } from '@/core/constants';
import ArrowRightIcon from "@/assets/svg/arrow-right.svg";
import { YTRef } from '../YoutubeEmbed/YoutubeEmbed';
import { PreviewVideoRef } from '../YoutubeEmbed/YoutubeVideoPreview';

import styles from './BasicEmbed.module.scss';
const cx = className.bind(styles);

interface BasicEmbedImagePreviewProps {
  videoUrl: string;
  thumbnail: AcfMediaFragment;
  className?: string;
  playVideoLabel?: string;
}

const BasicEmbedImagePreview = forwardRef<PreviewVideoRef, BasicEmbedImagePreviewProps>(({ thumbnail, videoUrl, className, playVideoLabel }, ref) => {
  const [showVideo, setShowVideo] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<YTRef>(null);
  useImperativeHandle(ref, () => ({
    getPlayer: () => videoRef.current?.getPlayer(),
    showEmbed: () => setShowVideo(true),
  }));
  const onClick = () => {
    setShowVideo(true);
    const player = videoRef.current?.getPlayer();
    player?.playVideo();
  };

  // handle parent's click
  useEffect(() => {
    if (!rootRef.current) return;
    const parent = rootRef.current.closest('button');
    if (parent) {
      parent.addEventListener('click', onClick);
    }
    return () => {
      if (parent) {
        parent.removeEventListener('click', onClick);
      }
    };
  }, []);

  return (
    <div className={cx(["basic-embed", className])} onClick={onClick} ref={rootRef}>
      <AnimatePresence>
        {!showVideo &&
          <motion.div
            className={cx("basic-embed__overlay")}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, pointerEvents: 'none' }}
            transition={{ duration: 0.5, ease: EASE.OutQuad }}
          >
            <Image image={thumbnail} className={cx("image")} />
            <IconButton
              className={cx("play-button")}
              icon={ArrowRightIcon}
              onClick={onClick}
              size="large"
              ariaLabel={`Play video ${playVideoLabel || ''}`}
              tag="div"
            />
          </motion.div>
        }
      </AnimatePresence>
      <BasicEmbed videoUrl={videoUrl} ref={videoRef} />
    </div>
  )
});

export default BasicEmbedImagePreview;